/**
 * Map tile prefetch + offline cache helper.
 *
 * The service worker serves tiles cache-first, so any tile fetched while online
 * (by browsing OR by prefetch below) is available offline afterwards.
 *
 * `prefetchArea` downloads every tile covering the given lat/lng bounds across a
 * range of zoom levels and stores them in the Cache Storage bucket the SW reads.
 */

export const TILE_CACHE_NAME = "pdam-tiles-v1";

// lng/lat → slippy map tile X/Y at zoom z
const lngToTileX = (lng, z) => Math.floor(((lng + 180) / 360) * Math.pow(2, z));
const latToTileY = (lat, z) => {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, z)
  );
};

const buildTileUrl = (template, subdomains, x, y, z) => {
  const s = subdomains && subdomains.length ? subdomains[(x + y) % subdomains.length] : "";
  return template
    .replace("{s}", s)
    .replace("{x}", x)
    .replace("{y}", y)
    .replace("{z}", z);
};

/**
 * Count how many tiles an area+zoom-range would require (for a confirm prompt).
 */
export const countAreaTiles = (bounds, minZoom, maxZoom) => {
  let total = 0;
  for (let z = minZoom; z <= maxZoom; z++) {
    const x0 = lngToTileX(bounds.west, z);
    const x1 = lngToTileX(bounds.east, z);
    const y0 = latToTileY(bounds.north, z);
    const y1 = latToTileY(bounds.south, z);
    total += (Math.abs(x1 - x0) + 1) * (Math.abs(y1 - y0) + 1);
  }
  return total;
};

/**
 * Prefetch all tiles for `bounds` (in {north,south,east,west}) across [minZoom,maxZoom].
 * Calls onProgress(done, total) periodically. Returns { done, total, failed }.
 */
export const prefetchArea = async (
  bounds,
  minZoom,
  maxZoom,
  template,
  subdomains,
  onProgress,
  maxTiles = 4000
) => {
  if (!("caches" in window)) {
    throw new Error("Cache Storage tidak didukung browser ini.");
  }
  const cache = await caches.open(TILE_CACHE_NAME);
  const urls = [];
  for (let z = minZoom; z <= maxZoom; z++) {
    const x0 = Math.min(lngToTileX(bounds.west, z), lngToTileX(bounds.east, z));
    const x1 = Math.max(lngToTileX(bounds.west, z), lngToTileX(bounds.east, z));
    const y0 = Math.min(latToTileY(bounds.north, z), latToTileY(bounds.south, z));
    const y1 = Math.max(latToTileY(bounds.north, z), latToTileY(bounds.south, z));
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        urls.push(buildTileUrl(template, subdomains, x, y, z));
        if (urls.length >= maxTiles) break;
      }
      if (urls.length >= maxTiles) break;
    }
    if (urls.length >= maxTiles) break;
  }

  const total = urls.length;
  let done = 0;
  let failed = 0;
  const CONCURRENCY = 6;

  const worker = async (slice) => {
    for (const url of slice) {
      try {
        // cross-origin tiles return opaque responses — still cacheable & usable by <img>
        const resp = await fetch(url, { mode: "no-cors" });
        await cache.put(url, resp);
      } catch {
        failed += 1;
      } finally {
        done += 1;
        if (onProgress && done % 10 === 0) onProgress(done, total);
      }
    }
  };

  // split into CONCURRENCY chunks
  const chunks = Array.from({ length: CONCURRENCY }, () => []);
  urls.forEach((u, i) => chunks[i % CONCURRENCY].push(u));
  await Promise.all(chunks.map(worker));
  if (onProgress) onProgress(done, total);
  return { done, total, failed };
};

export const getCachedTileCount = async () => {
  try {
    if (!("caches" in window)) return 0;
    const cache = await caches.open(TILE_CACHE_NAME);
    const keys = await cache.keys();
    return keys.length;
  } catch {
    return 0;
  }
};

export const clearTileCache = async () => {
  try {
    if (!("caches" in window)) return false;
    await caches.delete(TILE_CACHE_NAME);
    return true;
  } catch {
    return false;
  }
};
